import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createGroup, getPhoneGroups, updateGroup, deleteGroup, uploadPhoneNumbers, getGroupPhoneNumbers, getGroupPhoneNumbersAll, deletePhoneNumber, addManualPhoneNumbers, savePhoneNumber } from '../../../api/login.service';
import LoadingComponent from '../../../components/LoadingComponent';
import { IoMdAdd, IoMdSearch, IoMdCloudUpload, IoMdEye, IoMdCreate, IoMdTrash, IoMdPeople, IoMdCalendar, IoMdClose, IoMdCheckmark } from 'react-icons/io';

interface PhoneNumber {
  id?: string;
  phoneNumber?: string;
  name?: string;
  email?: string;
  tags?: string[];
  status?: string;
  addedAt?: string;
  [key: string]: any; // Allow dynamic keys
}

interface PhoneGroup {
  id: string;
  name: string;
  description?: string;
  phoneNumbers: PhoneNumber[];
  createdAt: string;
  updatedAt: string;
  totalCount: number;
}

interface UploadResult {
  success: number;
  failed: number;
  errors: string[];
}

export default function ModernPhoneGroupsManager() {
  const navigate = useNavigate();
  
  // Get wabaId from session storage
  const wabaId = sessionStorage.getItem('wabaId');
  
  // State for groups
  const [groups, setGroups] = useState<PhoneGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<PhoneGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for phone numbers display
  const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
  const [groupPhoneNumbers, setGroupPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);
  const [editingPhoneNumberId, setEditingPhoneNumberId] = useState<string | null>(null);
  const [editedPhoneNumbers, setEditedPhoneNumbers] = useState<Record<string, PhoneNumber>>({});
  
  // State for group operations
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: ''
  });
  
  // Upload states
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedGroupForUpload, setSelectedGroupForUpload] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'csv' | 'manual'>('csv');
  
  // Manual entry state
  interface ManualEntry {
    phoneNumber: string;
    name: string;
    status: string;
  }
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([
    { phoneNumber: '', name: '', status: 'active' }
  ]);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('all');

  // Fetch phone groups from API
  const fetchPhoneGroups = async () => {
    setLoading(true);
    try {
      if (!wabaId) {
        throw new Error('WABA ID is required');
      }

      // Call the actual API endpoint
      const apiResponse = await getPhoneGroups(wabaId);
      
      // Extract groups from the nested response structure
      const groupsData = apiResponse?.data?.groups || [];
      
      // Transform API response to match our PhoneGroup interface
      const transformedGroups: PhoneGroup[] = groupsData.map((group: any) => ({
        id: group.groupId || group.id || `group_${Date.now()}`,
        name: group.groupName || group.name || 'Unnamed Group',
        description: group.description || '',
        phoneNumbers: group.phoneNumbers || [],
        createdAt: group.createdAt || new Date().toISOString(),
        updatedAt: group.updatedAt || new Date().toISOString(),
        totalCount: group.phoneNumberCount || group.totalCount || group.phoneNumbers?.length || 0
      }));
      
      setGroups(transformedGroups);
      
    } catch (err: any) {
      console.error('Error fetching phone groups:', err);
      setError(err?.message || 'Failed to load phone groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhoneGroups();
  }, []);

  // Create new group
  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call the API to create the group
      const groupData = {
        groupName: groupForm.name.trim(),
        description: groupForm.description.trim(),
        wabaId: wabaId
      };

      const result = await createGroup(groupData);
      
      // Create local group object for UI
      const newGroup: PhoneGroup = {
        id: result.id || `group_${Date.now()}`,
        name: groupForm.name.trim(),
        description: groupForm.description.trim(),
        phoneNumbers: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        totalCount: 0
      };

      setGroups(prev => [...prev, newGroup]);
      setGroupForm({ name: '', description: '' });
      setShowCreateForm(false);
      setSuccess('Group created successfully!');
    } catch (err: any) {
      console.error('Error creating group:', err);
      setError(err?.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    try {
      setLoading(true);
      setError(null);

      await deleteGroup(selectedGroup.id);
      setGroups(prev => prev.filter(g => g.id !== selectedGroup.id));
      setSelectedGroup(null);
      setShowDeleteConfirm(false);
      setSuccess('Group deleted successfully!');
    } catch (err: any) {
      console.error('Error deleting group:', err);
      setError(err?.message || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV upload
  const handleUploadCSV = async () => {
    if (!uploadFile) {
      setError('Please select a CSV file');
      return;
    }

    if (!selectedGroupForUpload) {
      setError('Please select a group to upload to');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setUploadResult(null);

      // Validate CSV file
      if (!uploadFile.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please select a CSV file');
      }

      // Determine target group
      let targetGroup: PhoneGroup;
      
      if (selectedGroupForUpload === 'new') {
        // Create a new group for CSV upload
        const groupData = {
          groupName: `CSV Upload ${new Date().toLocaleDateString()}`,
          description: `Phone numbers uploaded from ${uploadFile.name}`,
          wabaId: wabaId
        };

        const result = await createGroup(groupData);
        targetGroup = {
          id: result.id || `group_${Date.now()}`,
          name: groupData.groupName,
          description: groupData.description,
          phoneNumbers: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          totalCount: 0
        };
        setGroups(prev => [...prev, targetGroup]);
      } else {
        // Use existing group
        targetGroup = groups.find(g => g.id === selectedGroupForUpload);
        if (!targetGroup) {
          throw new Error('Selected group not found');
        }
      }

      // Upload phone numbers (pass the CSV file directly)
      const uploadResult = await uploadPhoneNumbers(targetGroup.id, uploadFile);
      
      // Handle the API response
      if (uploadResult.success) {
        setUploadResult({
          success: uploadResult.successCount || 0,
          failed: uploadResult.failedCount || 0,
          errors: uploadResult.errors || []
        });
        
        // Refresh groups to show updated counts
        await fetchPhoneGroups();
        
        setSuccess(`Successfully uploaded phone numbers to "${targetGroup.name}"!`);
        
        // Close the modal and reset form
        setShowUploadForm(false);
        setUploadFile(null);
        setUploadResult(null);
        setSelectedGroupForUpload('');
        setActiveTab('csv');
        setManualEntries([{ phoneNumber: '', name: '', status: 'active' }]);
      } else {
        throw new Error(uploadResult.message || 'Upload failed');
      }
      
    } catch (err: any) {
      console.error('Error uploading CSV:', err);
      setError(err?.message || 'Failed to upload CSV file');
    } finally {
      setLoading(false);
    }
  };

  // Manual entry handlers
  const addManualEntry = () => {
    setManualEntries([...manualEntries, { phoneNumber: '', name: '', status: 'active' }]);
  };

  const removeManualEntry = (index: number) => {
    const newEntries = manualEntries.filter((_, i) => i !== index);
    if (newEntries.length === 0) {
      setManualEntries([{ phoneNumber: '', name: '', status: 'active' }]);
    } else {
      setManualEntries(newEntries);
    }
  };

  const updateManualEntry = (index: number, field: keyof ManualEntry, value: string) => {
    const newEntries = [...manualEntries];
    newEntries[index][field] = value;
    setManualEntries(newEntries);
  };

  const handleManualUpload = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('handleManualUpload called - using addManualPhoneNumbers API');
    
    setLoading(true);
    try {
      setError(null);
      setUploadResult(null);

      if (!selectedGroupForUpload) {
        throw new Error('Please select a group');
      }

      // Filter out empty entries
      const validEntries = manualEntries.filter(entry => entry.phoneNumber.trim() && entry.name.trim());
      
      if (validEntries.length === 0) {
        throw new Error('Please add at least one valid phone number entry');
      }

      // Determine target group
      let targetGroupId: string;
      
      if (selectedGroupForUpload === 'new') {
        // Create a new group for manual entries
        const groupData = {
          groupName: `Manual Entry ${new Date().toLocaleDateString()}`,
          description: 'Phone numbers entered manually',
          wabaId: wabaId
        };

        const result = await createGroup(groupData);
        targetGroupId = result.id || `group_${Date.now()}`;
        
        // Add the new group to the list
        const newGroup: PhoneGroup = {
          id: targetGroupId,
          name: groupData.groupName,
          description: groupData.description,
          phoneNumbers: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          totalCount: 0
        };
        setGroups(prev => [...prev, newGroup]);
      } else {
        targetGroupId = selectedGroupForUpload;
      }

      // Validate group ID
      if (!targetGroupId || targetGroupId.trim() === '') {
        throw new Error('Group ID is missing. Please select a group.');
      }

      console.log('=== ADDING MANUAL PHONE NUMBERS ===');
      console.log('Group ID:', targetGroupId);
      console.log('Group ID type:', typeof targetGroupId);
      console.log('Valid entries count:', validEntries.length);
      console.log('Calling addManualPhoneNumbers with groupId:', targetGroupId, 'and entries:', validEntries);
      
      // Call the manual phone numbers API - NOT uploadPhoneNumbers
      const result = await addManualPhoneNumbers(targetGroupId, validEntries);
      
      console.log('addManualPhoneNumbers response:', result);
      
      // Handle the API response
      if (result.success !== false) {
        const successCount = result.successCount || validEntries.length;
        const failedCount = result.failedCount || 0;
        
        setUploadResult({
          success: successCount,
          failed: failedCount,
          errors: result.errors || []
        });
        
        // Refresh groups to show updated counts
        await fetchPhoneGroups();
        
        setSuccess(`Successfully added ${successCount} phone number${successCount !== 1 ? 's' : ''}!`);
        setShowUploadForm(false);
        setManualEntries([{ phoneNumber: '', name: '', status: 'active' }]);
        setActiveTab('csv');
      } else {
        throw new Error(result.message || 'Failed to add phone numbers');
      }
      
    } catch (err: any) {
      console.error('Error adding manual phone numbers:', err);
      setError(err?.message || 'Failed to add phone numbers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch phone numbers for a group
  const fetchGroupPhoneNumbers = async (groupId: string) => {
    setPhoneNumbersLoading(true);
    try {
      setError(null);
      console.log('Fetching phone numbers for groupId:', groupId);
      
      const response = await getGroupPhoneNumbersAll(groupId);
      console.log('Raw API response from getGroupPhoneNumbersAll:', response);
      
      // Handle different response structures - try multiple paths
      let phoneNumbers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        phoneNumbers = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          // response.data is an array
          phoneNumbers = response.data;
        } else if (response.data?.phoneNumbers && Array.isArray(response.data.phoneNumbers)) {
          // response.data.phoneNumbers is an array
          phoneNumbers = response.data.phoneNumbers;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // Nested data structure
          phoneNumbers = response.data.data;
        }
      } else if (response?.phoneNumbers && Array.isArray(response.phoneNumbers)) {
        // response.phoneNumbers is an array
        phoneNumbers = response.phoneNumbers;
      } else if (response?.results && Array.isArray(response.results)) {
        // response.results is an array
        phoneNumbers = response.results;
      } else if (response && typeof response === 'object') {
        // Try to extract array from object values
        const values = Object.values(response);
        const arrayValue = values.find(v => Array.isArray(v));
        if (arrayValue) {
          phoneNumbers = arrayValue as any[];
        }
      }
      
      console.log('Extracted phoneNumbers:', phoneNumbers);
      
      // Ensure we have an array
      if (!Array.isArray(phoneNumbers)) {
        console.warn('phoneNumbers is not an array, defaulting to empty array');
        phoneNumbers = [];
      }
      
      // Ensure each phone number has a unique ID
      const phoneNumbersWithIds = phoneNumbers.map((phone: any, index: number) => ({
        ...phone,
        id: phone.id || phone.phoneNumberId || phone.phoneNumberId || `phone_${index}_${Date.now()}`
      }));
      
      console.log('Phone numbers with IDs:', phoneNumbersWithIds);
      setGroupPhoneNumbers(phoneNumbersWithIds);
      setEditingPhoneNumberId(null);
      setEditedPhoneNumbers({});
    } catch (err: any) {
      console.error('Error fetching group phone numbers:', err);
      console.error('Error details:', {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      setError(err?.message || 'Failed to load phone numbers');
      setGroupPhoneNumbers([]);
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  // Handle View Details click
  const handleViewDetails = async (group: PhoneGroup) => {
    setSelectedGroup(group);
    setShowPhoneNumbers(true);
    await fetchGroupPhoneNumbers(group.id);
  };

  // Handle edit button click
  const handleEditPhoneNumber = (phoneNumber: PhoneNumber) => {
    setEditingPhoneNumberId(phoneNumber.id || '');
    setEditedPhoneNumbers({
      [phoneNumber.id || '']: { ...phoneNumber }
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingPhoneNumberId(null);
    setEditedPhoneNumbers({});
  };

  // Handle save edit
  const handleSaveEdit = async (phoneNumberId: string) => {
    if (!selectedGroup) return;
    
    const editedData = editedPhoneNumbers[phoneNumberId];
    if (!editedData) return;

    // Ensure phoneNumber field is present (required field)
    if (!editedData.phoneNumber) {
      setError('Phone number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Prepare data for API call - include all fields
      const phoneNumberData = {
        phoneNumber: editedData.phoneNumber,
        name: editedData.name || '',
        status: editedData.status || 'active',
        course: editedData.course || '',
        date: editedData.date || '',
        image: editedData.image || '',
        ...editedData // Include any other fields
      };
      
      // Call API to save/update phone number (backend handles create/update logic)
      await savePhoneNumber(selectedGroup.id, phoneNumberData);
      
      // Refresh the phone numbers list to get updated data
      await fetchGroupPhoneNumbers(selectedGroup.id);
      
      setEditingPhoneNumberId(null);
      setEditedPhoneNumbers({});
      setSuccess('Phone number saved successfully!');
    } catch (err: any) {
      console.error('Error saving phone number:', err);
      setError(err?.message || 'Failed to save phone number');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete phone number
  const handleDeletePhoneNumber = async (phoneNumberId: string) => {
    if (!selectedGroup) return;
    
    const phoneNumber = groupPhoneNumbers.find(p => p.id === phoneNumberId);
    if (!phoneNumber) return;
    
    const phoneNumberValue = phoneNumber.phoneNumber || phoneNumberId;
    if (!window.confirm(`Are you sure you want to delete phone number ${phoneNumberValue}?`)) return;

    try {
      setLoading(true);
      setError(null);
      
      // Call delete API
      await deletePhoneNumber(selectedGroup.id, phoneNumberValue);
      
      // Update local state
      setGroupPhoneNumbers(prev => prev.filter(phone => phone.id !== phoneNumberId));
      setSuccess('Phone number deleted successfully!');
      
      // Refresh the phone numbers list
      await fetchGroupPhoneNumbers(selectedGroup.id);
    } catch (err: any) {
      console.error('Error deleting phone number:', err);
      setError(err?.message || 'Failed to delete phone number');
    } finally {
      setLoading(false);
    }
  };

  // Handle field change in edit mode
  const handleFieldChange = (phoneNumberId: string, field: string, value: any) => {
    setEditedPhoneNumbers(prev => ({
      ...prev,
      [phoneNumberId]: {
        ...prev[phoneNumberId],
        [field]: value
      }
    }));
  };

  // Get all keys from phone numbers (dynamic fields)
  const getAllPhoneNumberKeys = (): string[] => {
    if (groupPhoneNumbers.length === 0) return [];
    
    const allKeys = new Set<string>();
    groupPhoneNumbers.forEach(phone => {
      Object.keys(phone).forEach(key => {
        if (key !== 'id') allKeys.add(key);
      });
    });
    
    return Array.from(allKeys);
  };

  // Check if a value is an image URL
  const isImageUrl = (value: any): boolean => {
    if (!value || typeof value !== 'string') return false;
    if (value === '--' || value.trim() === '') return false;
    
    // Check if it starts with http:// or https://
    const isHttpUrl = /^https?:\/\//i.test(value);
    if (!isHttpUrl) return false;
    
    // Check if it's a URL
    try {
      const url = new URL(value);
      // Check if it's an image URL by extension
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
      const hasImageExtension = imageExtensions.some(ext => 
        url.pathname.toLowerCase().endsWith(ext)
      );
      
      // Also check if URL contains 'image' in path (like the example: media.frontdesk24.com/user-media/image/...)
      const hasImageInPath = url.pathname.toLowerCase().includes('image') || 
                            url.pathname.toLowerCase().includes('/image/');
      
      return hasImageExtension || hasImageInPath;
    } catch {
      // If URL parsing fails, check if it looks like an image URL by pattern
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(value) || 
             /\/image\//i.test(value);
    }
  };

  // Filter groups based on search term
  const filteredGroups = groups.filter(group => 
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingComponent loading={true} message="Loading phone groups..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Phone Groups Manager</h1>
              <p className="text-gray-600 text-lg">Manage your WhatsApp phone number groups for marketing campaigns</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">{groups.length}</p>
                <p className="text-sm text-gray-500">Total Groups</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">{groups.reduce((sum, group) => sum + group.totalCount, 0)}</p>
                <p className="text-sm text-gray-500">Total Numbers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <IoMdCheckmark className="w-5 h-5 text-green-600 mr-3" />
              <p className="text-green-800 font-medium">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <IoMdClose className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                <IoMdAdd className="w-5 h-5 mr-2" />
                Create Group
              </button>
            </div>
          </div>
        </div>

        {/* Groups Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Phone Groups ({filteredGroups.length})</h2>
            {filteredGroups.length > 0 && (
              <div className="text-sm text-gray-500">
                Showing {filteredGroups.length} of {groups.length} groups
              </div>
            )}
          </div>

          {filteredGroups.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <IoMdPeople className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Groups Found</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {searchTerm ? 'No groups match your search criteria.' : 'Create your first phone group to get started with marketing campaigns.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                >
                  <IoMdAdd className="w-5 h-5 mr-2" />
                  Create Your First Group
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <div key={group.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                        {group.name}
                      </h3>
                      {group.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{group.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleViewDetails(group)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Numbers"
                      >
                        <IoMdEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Group"
                      >
                        <IoMdTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <IoMdPeople className="w-4 h-4 mr-2 text-blue-500" />
                      <span className="font-medium">{group.totalCount}</span>
                      <span className="ml-1">phone numbers</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <IoMdCalendar className="w-4 h-4 mr-2 text-green-500" />
                      <span>Created {new Date(group.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-gray-500">Active</span>
                      </div>
                      <button
                        onClick={() => handleViewDetails(group)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View Details →
                      </button>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          console.log('Opening Add Phone Numbers modal for group:', group.id, group.name);
                          setSelectedGroupForUpload(group.id);
                          setShowUploadForm(true);
                        }}
                        className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium text-sm shadow-md hover:shadow-lg"
                      >
                        <IoMdAdd className="w-4 h-4 mr-2" />
                        Add Phone Numbers
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Create New Group</h3>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setGroupForm({ name: '', description: '' });
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group Name *</label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="Enter group name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                  placeholder="Enter group description (optional)"
                />
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setGroupForm({ name: '', description: '' });
                }}
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={loading || !groupForm.name.trim()}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                {loading ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Delete Group</h3>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedGroup(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <IoMdTrash className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 mb-2">
                    Are you sure you want to delete <span className="font-semibold text-gray-900">"{selectedGroup.name}"</span>?
                  </p>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone and will permanently remove the group and all its phone numbers.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedGroup(null);
                }}
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                {loading ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload CSV Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Add Phone Numbers</h3>
                <button
                  onClick={() => {
                    setShowUploadForm(false);
                    setUploadFile(null);
                    setUploadResult(null);
                    setSelectedGroupForUpload('');
                    setActiveTab('csv');
                    setManualEntries([{ phoneNumber: '', name: '', status: 'active' }]);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
              
              {/* Tabs */}
              <div className="flex mt-4 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('csv')}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'csv'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📄 Upload CSV
                </button>
                <button
                  onClick={() => setActiveTab('manual')}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'manual'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  ✏️ Manual Entry
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Select Group - Common for both tabs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                <select
                  value={selectedGroupForUpload || ''}
                  onChange={(e) => setSelectedGroupForUpload(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50"
                  disabled={!!selectedGroupForUpload && selectedGroupForUpload !== 'new'}
                >
                  <option value="">Choose a group...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.totalCount} numbers)
                    </option>
                  ))}
                  <option value="new">Create New Group</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedGroupForUpload && selectedGroupForUpload !== 'new'
                    ? `Phone numbers will be added to: ${groups.find(g => g.id === selectedGroupForUpload)?.name || 'selected group'}`
                    : 'Select which group to add the phone numbers to'}
                </p>
              </div>
              
              {/* CSV Upload Tab Content */}
              {activeTab === 'csv' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
                      <IoMdCloudUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                          Click to upload CSV file
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          or drag and drop your file here
                        </p>
                      </label>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        CSV format: phoneNumber,name,status
                      </p>
                      <a
                        href="/sample-phone-numbers.csv"
                        download="sample-phone-numbers.csv"
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        📥 Download sample CSV
                      </a>
                    </div>
                  </div>
                  
                  {uploadFile && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <IoMdCheckmark className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">{uploadFile.name}</p>
                          <p className="text-xs text-blue-700">
                            Size: {(uploadFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Manual Entry Tab Content */}
              {activeTab === 'manual' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                    <button
                      onClick={addManualEntry}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <IoMdAdd className="w-4 h-4" />
                      Add Row
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {manualEntries.map((entry, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="grid grid-cols-12 gap-3 items-start">
                          <div className="col-span-10 grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Phone Number</label>
                              <input
                                type="text"
                                value={entry.phoneNumber}
                                onChange={(e) => updateManualEntry(index, 'phoneNumber', e.target.value)}
                                placeholder="+1234567890"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Name</label>
                              <input
                                type="text"
                                value={entry.name}
                                onChange={(e) => updateManualEntry(index, 'name', e.target.value)}
                                placeholder="John Doe"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Status</label>
                              <select
                                value={entry.status}
                                onChange={(e) => updateManualEntry(index, 'status', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-span-2 flex items-end justify-end">
                            <button
                              onClick={() => removeManualEntry(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove row"
                            >
                              <IoMdTrash className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      <strong>{manualEntries.filter(e => e.phoneNumber.trim() && e.name.trim()).length}</strong> valid entries
                    </p>
                  </div>
                </div>
              )}

              {/* Upload Results - Common for both tabs */}
              {uploadResult && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-green-800 mb-2">Upload Results:</h4>
                  <div className="space-y-1">
                    <p className="text-sm text-green-700">
                      ✅ Successfully uploaded: {uploadResult.success} numbers
                    </p>
                    {uploadResult.failed > 0 && (
                      <p className="text-sm text-red-700">
                        ❌ Failed: {uploadResult.failed} numbers
                      </p>
                    )}
                    {uploadResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-red-600 font-medium">Errors:</p>
                        <ul className="text-xs text-red-600 list-disc list-inside">
                          {uploadResult.errors.slice(0, 3).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {uploadResult.errors.length > 3 && (
                            <li>... and {uploadResult.errors.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadFile(null);
                  setUploadResult(null);
                  setSelectedGroupForUpload('');
                  setActiveTab('csv');
                  setManualEntries([{ phoneNumber: '', name: '', status: 'active' }]);
                }}
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              {activeTab === 'csv' ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('CSV Upload button clicked');
                    handleUploadCSV();
                  }}
                  disabled={!uploadFile || !selectedGroupForUpload || loading}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Uploading...' : 'Upload CSV'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Add Numbers button clicked - activeTab is:', activeTab);
                    handleManualUpload(e);
                  }}
                  disabled={!selectedGroupForUpload || manualEntries.filter(e => e.phoneNumber.trim() && e.name.trim()).length === 0 || loading}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Adding...' : 'Add Numbers'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Numbers Modal */}
      {showPhoneNumbers && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Phone Numbers in "{selectedGroup.name}"
                </h3>
                <button
                  onClick={() => {
                    setShowPhoneNumbers(false);
                    setGroupPhoneNumbers([]);
                    setSelectedGroup(null);
                    setEditingPhoneNumberId(null);
                    setEditedPhoneNumbers({});
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <IoMdClose className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {phoneNumbersLoading ? (
                <LoadingComponent loading={true} message="Loading phone numbers..." />
              ) : groupPhoneNumbers.length === 0 ? (
                <div className="text-center py-12">
                  <IoMdPeople className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No phone numbers found in this group</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {getAllPhoneNumberKeys().map(key => (
                          <th
                            key={key}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {key}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupPhoneNumbers.map((phone, index) => {
                        const isEditing = editingPhoneNumberId === phone.id;
                        const editedData = editedPhoneNumbers[phone.id || ''] || phone;
                        
                        return (
                          <tr 
                            key={phone.id || index} 
                            className={`hover:bg-gray-50 ${isEditing ? 'bg-blue-50' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (!isEditing) {
                                handleEditPhoneNumber(phone);
                              }
                            }}
                          >
                            {getAllPhoneNumberKeys().map(key => {
                              const cellValue = phone[key];
                              const isImage = !isEditing && isImageUrl(cellValue);
                              
                              return (
                              <td 
                                key={key} 
                                className={`px-4 py-3 text-sm text-gray-900 ${isImage ? '' : 'whitespace-nowrap'}`}
                                onClick={(e) => {
                                  // Prevent row click when clicking on input/select
                                  if (isEditing) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                {isEditing ? (
                                  key.toLowerCase() === 'status' ? (
                                    <select
                                      value={editedData[key] || ''}
                                      onChange={(e) => handleFieldChange(phone.id || '', key, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="active">active</option>
                                      <option value="inactive">inactive</option>
                                      <option value="pending">pending</option>
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={editedData[key] || ''}
                                      onChange={(e) => handleFieldChange(phone.id || '', key, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  )
                                ) : (
                                  (() => {
                                    const cellValue = phone[key];
                                    // Check if this is an image URL
                                    if (isImageUrl(cellValue)) {
                                      return (
                                        <div className="flex items-center justify-center">
                                          <img
                                            src={cellValue}
                                            alt="Phone number image"
                                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                            onError={(e) => {
                                              // Fallback to URL text if image fails to load
                                              const target = e.target as HTMLImageElement;
                                              target.style.display = 'none';
                                              const parent = target.parentElement;
                                              if (parent) {
                                                parent.innerHTML = `<span class="text-xs text-gray-500 break-all">${cellValue}</span>`;
                                              }
                                            }}
                                          />
                                        </div>
                                      );
                                    }
                                    // Regular text display
                                    return <span>{cellValue || '--'}</span>;
                                  })()
                                )}
                              </td>
                            );
                            })}
                            <td 
                              className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isEditing ? (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveEdit(phone.id || '');
                                    }}
                                    className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50"
                                    title="Save"
                                  >
                                    <IoMdCheckmark className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelEdit();
                                    }}
                                    className="text-gray-600 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                                    title="Cancel"
                                  >
                                    <IoMdClose className="w-5 h-5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPhoneNumber(phone);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                                    title="Edit"
                                  >
                                    <IoMdCreate className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePhoneNumber(phone.id || '');
                                    }}
                                    className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                    title="Delete"
                                  >
                                    <IoMdTrash className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
